import { useCallback, useState } from 'react';

import { Undef } from 'types/UtilityTypes';
import type { LoadBuildingSuggestions } from 'types/suggestions/LoadBuildingSuggestions';

import { useSharepointLoadBuildingGraphState } from '../providers/context';
import {
  type SharepointWriteStatus,
  writeSharepointLoadIdToWorkbook,
} from '../utils/writeSharepointLoadIdToWorkbook';

export type { SharepointWriteStatus };

export function useSharepointWriteback() {
  const [sharepointWriteStatus, setSharepointWriteStatus] =
    useState<Undef<SharepointWriteStatus>>(undefined);
  const { graphMeta, refreshGraphRowsBypassCache } =
    useSharepointLoadBuildingGraphState();

  const resetWriteStatus = useCallback(() => {
    setSharepointWriteStatus(undefined);
  }, []);

  const runSharepointWritebackAfterCreate = useCallback(
    async (params: {
      loadId: string;
      activeLoadBuildingSuggestion: LoadBuildingSuggestions | undefined;
    }) => {
      const { loadId, activeLoadBuildingSuggestion } = params;
      setSharepointWriteStatus(undefined);

      const writeback = activeLoadBuildingSuggestion?.sharepointWriteback;
      const worksheetName = graphMeta?.worksheetName?.trim() ?? '';
      if (!loadId.trim() || !writeback?.gridRowIndices.length) {
        return;
      }

      const targetLabel = writeback.targetLabel.trim() || 'workbook';
      setSharepointWriteStatus({ phase: 'pending', targetLabel });
      const writeResult = await writeSharepointLoadIdToWorkbook({
        loadId,
        templateId: writeback.templateId,
        gridRowIndices: writeback.gridRowIndices,
        worksheetName,
      });
      if (writeResult.ok) {
        setSharepointWriteStatus({ phase: 'success', targetLabel });
        refreshGraphRowsBypassCache?.();
        return;
      }
      setSharepointWriteStatus({
        phase: 'error',
        stage: writeResult.stage,
        targetLabel,
      });
    },
    [graphMeta?.worksheetName, refreshGraphRowsBypassCache]
  );

  return {
    sharepointWriteStatus,
    resetWriteStatus,
    runSharepointWritebackAfterCreate,
  };
}
